import React, { useState, useEffect, useMemo, useRef } from 'react';
import uniqid from 'uniqid';

import Loader from 'components/partials/loader/loader';
import { getBlocksFromContent } from 'containers/user/learn/heplers/get-blocks-from-content';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import LessonIndicator from 'containers/user/learn/containers/content/lesson/lesson-indicator/lesson-indicator';
import LessonNavigation from 'containers/user/learn/containers/content/lesson/lesson-navigation/lesson-navigation';

import api from 'api';
import { useCurrentUser } from 'hooks/api/use-current-user';

interface LessonData {
  name?: string;
  content?: Array<{
    id: string | number;
    type: string;
    [key: string]: unknown;
  }>;
}

interface ProgressUpdate {
  lessonId: string | number;
  progress: number;
}

interface LessonClickArgs {
  to: string;
  id: number;
}

interface LessonProps {
  lessonId: string | number;
  onProgressUpdate: (update: ProgressUpdate) => void;
  handleLessonClick: (args: LessonClickArgs) => void;
  courseId: string | number;
  lessonsIds: string[];
  progress?: number;
  isImpersonatingUser?: boolean;
}

const Lesson = ({
  lessonId,
  onProgressUpdate,
  handleLessonClick,
  courseId,
  lessonsIds,
  progress = 0,
  isImpersonatingUser,
}: LessonProps): React.JSX.Element => {
  const [lesson, setLesson] = useState<LessonData>({});
  const [loading, setLoading] = useState<boolean>(false);
  const lessonRef = useRef<HTMLDivElement>(null);

  const prevScrollTopRef = useRef<number>(0);
  const savedProgressRef = useRef<number>(0);

  const user = useCurrentUser();

  const memoizedContent = useMemo(
    () =>
      lesson.content?.map((component) =>
        getBlocksFromContent({ ...component, key: uniqid() })
      ),
    [lesson.content]
  );

  const calculateScrollDistance = (): void => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;

    const footerHeight =
      (document.querySelector('.global-footer') as HTMLElement | null)
        ?.offsetHeight || 0;
    const docHeight = document.documentElement.scrollHeight - footerHeight;

    if (
      lessonRef.current &&
      lessonRef.current.clientHeight > 100 &&
      docHeight <= window.innerHeight
    ) {
      savedProgressRef.current = 100;
      onProgressUpdate({ lessonId, progress: 100 });
    } else if (scrollTop > prevScrollTopRef.current) {
      const scrollPosition =
        docHeight > 0
          ? Math.min(
              Math.floor((scrollTop / (docHeight - window.innerHeight)) * 100),
              100
            )
          : 100;

      savedProgressRef.current = Math.max(
        Math.ceil(scrollPosition),
        savedProgressRef.current
      );

      onProgressUpdate({ lessonId, progress: savedProgressRef.current });
    }

    prevScrollTopRef.current = scrollTop;
  };

  useEffect(() => {
    if (isImpersonatingUser) {
      return undefined;
    }

    window.addEventListener('scroll', calculateScrollDistance);

    return () => {
      window.removeEventListener('scroll', calculateScrollDistance);
    };
  }, [lessonId, isImpersonatingUser]);

  useEffect(() => {
    setLoading(true);
    api
      .getLesson(lessonId, user?.data?.oktaUserId ?? '')
      .then((res) => {
        setLesson(res.data.data.attributes);
      })
      .catch(console.error)
      .finally(() => {
        setLoading(false);
      });
  }, [lessonId]);

  useEffect(() => {
    savedProgressRef.current = progress;
  }, [lessonId]);

  useEffect(() => {
    if (isImpersonatingUser) {
      return;
    }

    if (!loading) {
      calculateScrollDistance();
    }
  }, [lesson.content, loading, isImpersonatingUser]);

  return (
    <ResponsiveWrapper ref={lessonRef}>
      {loading ? (
        <Loader {...{ loading }} fullScreen={false} overlay="transparent" />
      ) : (
        <>
          <LessonIndicator
            currentLessonNumber={lessonsIds.findIndex(
              (id) => id == `lesson-${lessonId}`
            )}
            totalLessons={lessonsIds.length}
          />
          <h2 className="text-neutral-950 font-semibold text-2xl mb-6">
            {lesson.name}
          </h2>
          {memoizedContent}
          <LessonNavigation
            {...{
              currentLessonId: lessonId,
              handleLessonClick,
              courseId,
              lessons: lessonsIds,
            }}
          />
        </>
      )}
    </ResponsiveWrapper>
  );
};

export default Lesson;
